import { systemModelsAPI } from '@/utils/api';
import { normalizePersistedImageUrl, uploadsPathFromSignedViewUrl } from '@/utils/imageUrlUtils';
import {
  buildStaticModelEthnicityPresets,
  findModelEthnicityPresetById,
  inferModelEthnicityPresetIdFromAssetUrl,
  isModelEthnicitySystemPresetAssetUrl,
} from '@/pages/ipe/workbench/constants/modelEthnicityPresetConfig';
import {
  buildTryOnSystemModelSelectionParams,
  buildTryOnTaskModelParams,
  buildTryOnUploadedModelSelectionParams,
  findTryOnPresetInListByUrl,
  isTryOnSystemPresetSelectorPaused,
  resolveTryOnPresetCardDisplaySrc,
  resolveTryOnPresetPersistedUrl,
} from '@/pages/ipe/workbench/utils/tryOnSystemModelPresetFallback';

/** 节点 13.1：手持 / 万物穿戴系统模特 featureCode */
export const SYSTEM_MODEL_ETHNICITY_FEATURE = {
  modelHandheld: 'modelHandheld',
  modelUniversalWear: 'modelUniversalWear',
};

export const MODEL_ETHNICITY_PRESET_SOURCE = {
  API: 'api',
  STATIC: 'static',
  EMPTY: 'empty',
};

const DEFAULT_ETHNICITY_PRESET_PAGE_SIZE = 20;

/** 节点 13.2 / 14.2：modelType + ethnicity 组合键 */
export function buildModelEthnicityPresetComboKey(modelType, ethnicity) {
  const model = String(modelType || '').trim();
  const eth = String(ethnicity || '').trim();
  if (!model || !eth) return '';
  return `${model}|${eth}`;
}

/** 节点 13.2 / 14.2：preset 是否属于当前 modelType + ethnicity 组合 */
export function isModelEthnicityPresetForCombo(preset, modelType, ethnicity) {
  if (!preset) return false;
  const model = String(modelType || '').trim();
  const eth = String(ethnicity || '').trim();
  if (!model || !eth) return false;
  return String(preset.modelType || '').trim() === model
    && String(preset.ethnicity || '').trim() === eth;
}

function resolveItemSortOrder(item) {
  const topLevel = Number(item?.sortOrder);
  if (Number.isFinite(topLevel)) return topLevel;
  const attrLevel = Number(item?.attributes?.sortOrder);
  if (Number.isFinite(attrLevel)) return attrLevel;
  return Number.MAX_SAFE_INTEGER;
}

/** 对齐后端 SystemModelStudioAlignment.modelEthnicityPresetComparator */
export function compareModelEthnicityPresetsBySortOrder(a, b) {
  const sortDiff = resolveItemSortOrder(a) - resolveItemSortOrder(b);
  if (sortDiff !== 0) return sortDiff;

  const updatedA = String(a?.updatedAt || '');
  const updatedB = String(b?.updatedAt || '');
  if (updatedA !== updatedB) {
    return updatedB.localeCompare(updatedA);
  }

  return String(a?.id || '').localeCompare(String(b?.id || ''));
}

export function sortModelEthnicityPresets(items = []) {
  return [...items].sort(compareModelEthnicityPresetsBySortOrder);
}

function readEthnicityPresetAttributes(item = {}) {
  const attrs = item?.attributes && typeof item.attributes === 'object'
    ? item.attributes
    : {};
  return {
    modelType: attrs.modelType || item.modelType || '',
    ethnicity: attrs.ethnicity || item.ethnicity || '',
    sortOrder: item.sortOrder ?? attrs.sortOrder ?? 0,
  };
}

function resolveEthnicityPresetApiUrl(item = {}) {
  const candidates = [item.url, item.src, item.displayUrl, item.viewUrl];
  for (const candidate of candidates) {
    const signed = uploadsPathFromSignedViewUrl(candidate);
    if (signed) return signed;
    const persisted = normalizePersistedImageUrl(candidate);
    if (persisted) return persisted;
  }
  return '';
}

/** 对齐后端 SystemModelStudioAlignment.toModelEthnicityPresetView */
export function mapSystemModelItemToEthnicityPresetView(item) {
  if (!item) return null;
  const attrs = readEthnicityPresetAttributes(item);
  const apiId = String(item.id || '').trim();
  const legacyPresetId = String(item.legacyPresetId || '').trim();
  const presetId = legacyPresetId || apiId;
  if (!presetId) return null;

  const apiUrl = resolveEthnicityPresetApiUrl(item);
  const displayUrl = String(item.displayUrl || item.viewUrl || '').trim();

  const view = {
    id: presetId,
    modelType: attrs.modelType,
    ethnicity: attrs.ethnicity,
    sortOrder: attrs.sortOrder,
    url: apiUrl || displayUrl,
    displayUrl: item.displayUrl || '',
    viewUrl: item.viewUrl || '',
    assetRev: item.assetRev,
    source: 'system',
  };
  if (apiId) {
    view.apiId = apiId;
  }
  if (legacyPresetId) {
    view.legacyPresetId = legacyPresetId;
  }
  if (apiUrl) {
    view.apiUrl = apiUrl;
  }
  return view;
}

export function mapPublishedItemsToModelEthnicityPresets(items = []) {
  if (!Array.isArray(items)) return [];
  return sortModelEthnicityPresets(
    items.map(mapSystemModelItemToEthnicityPresetView).filter(Boolean),
  );
}

export function filterSelectableModelEthnicityPresets(presets = []) {
  if (!Array.isArray(presets)) return [];
  return presets.filter((preset) => {
    if (preset?.isEmpty || preset?.selectable === false) return false;
    return Boolean(resolveTryOnPresetCardDisplaySrc(preset));
  });
}

/** 节点 13.2 / 14.2：仅保留当前 modelType + ethnicity 组合下的 preset */
export function filterModelEthnicityPresetsForCombo(presets = [], modelType, ethnicity) {
  if (!Array.isArray(presets)) return [];
  return presets.filter((preset) => isModelEthnicityPresetForCombo(preset, modelType, ethnicity));
}

export function clearInvalidModelEthnicityPreset(updates, prev, modelType, ethnicity, presets = []) {
  const presetId = String(prev?.selectedPresetId || '').trim();
  if (!presetId) return updates;
  const matched = findModelEthnicityPresetInList(presets, presetId)
    || findModelEthnicityPresetById(modelType, ethnicity, presetId);
  if (matched && isModelEthnicityPresetForCombo(matched, modelType, ethnicity)) {
    return updates;
  }
  return {
    ...updates,
    selectedPresetId: '',
    selectedPresetUrl: '',
    usedUploadedModel: false,
  };
}

export function buildModelEthnicityPresetDisplaySlots(presets = [], modelType, ethnicity) {
  const sorted = sortModelEthnicityPresets(
    filterModelEthnicityPresetsForCombo(presets, modelType, ethnicity),
  );
  return sorted.map((preset, index) => {
    const hasImage = Boolean(resolveTryOnPresetCardDisplaySrc(preset));
    return {
      ...preset,
      presetIndex: index,
      slotIndex: index + 1,
      ethnicityLabel: `系统模特 ${index + 1}`,
      isEmpty: !hasImage,
      selectable: hasImage,
    };
  });
}

function buildModelEthnicityPresetsQuery({ featureCode, modelType, ethnicity } = {}) {
  return {
    featureCode: String(featureCode || '').trim(),
    modelType: String(modelType || '').trim(),
    ethnicity: String(ethnicity || '').trim(),
    page: 1,
    pageSize: DEFAULT_ETHNICITY_PRESET_PAGE_SIZE,
  };
}

/**
 * 节点 13.1 / 14.1：手持 / 万物穿戴系统模特 — GET /api/system/models。
 * @returns {Promise<{ presets: Array, source: string }>}
 */
export async function fetchModelEthnicityPresets({
  featureCode,
  modelType,
  ethnicity,
} = {}) {
  const feature = String(featureCode || '').trim();
  const model = String(modelType || '').trim();
  const eth = String(ethnicity || '').trim();

  if (!feature || !model || !eth) {
    return {
      presets: [],
      source: MODEL_ETHNICITY_PRESET_SOURCE.EMPTY,
    };
  }

  const response = await systemModelsAPI.list(buildModelEthnicityPresetsQuery({
    featureCode: feature,
    modelType: model,
    ethnicity: eth,
  }));
  const apiPresets = mapPublishedItemsToModelEthnicityPresets(response?.items);
  return {
    presets: apiPresets,
    source: apiPresets.length > 0
      ? MODEL_ETHNICITY_PRESET_SOURCE.API
      : MODEL_ETHNICITY_PRESET_SOURCE.EMPTY,
    staleCacheUsed: Boolean(response?.staleCacheUsed),
  };
}

export function buildStaticModelEthnicityPresetFallback(modelType, ethnicity) {
  return buildStaticModelEthnicityPresets(modelType, ethnicity);
}

/**
 * 节点 13.4 / 14.4 / 16.5：API 无数据或故障时 fallback 到 buildStaticModelEthnicityPresets。
 * @returns {Promise<{ presets: Array, source: string, fallbackUsed: boolean }>}
 */
export async function fetchModelEthnicityPresetsWithStaticFallback({
  featureCode,
  modelType,
  ethnicity,
} = {}) {
  const feature = String(featureCode || '').trim();
  const model = String(modelType || '').trim();
  const eth = String(ethnicity || '').trim();
  const staticPresets = buildStaticModelEthnicityPresetFallback(model, eth);

  if (!feature || !model || !eth) {
    return {
      presets: [],
      source: MODEL_ETHNICITY_PRESET_SOURCE.EMPTY,
      fallbackUsed: false,
    };
  }

  try {
    const apiResult = await fetchModelEthnicityPresets({ featureCode: feature, modelType: model, ethnicity: eth });
    if (apiResult.presets.length > 0) {
      return {
        presets: apiResult.presets,
        source: MODEL_ETHNICITY_PRESET_SOURCE.API,
        fallbackUsed: Boolean(apiResult.staleCacheUsed),
      };
    }
  } catch {
    // API 不可用时走静态 fallback（节点 13.4 / 14.4 / 16.5）
  }

  return {
    presets: staticPresets,
    source: staticPresets.length > 0
      ? MODEL_ETHNICITY_PRESET_SOURCE.STATIC
      : MODEL_ETHNICITY_PRESET_SOURCE.EMPTY,
    fallbackUsed: staticPresets.length > 0,
  };
}

export function findModelEthnicityPresetInList(presets = [], presetId) {
  const id = String(presetId || '').trim();
  if (!id) return null;
  return presets.find((item) => {
    const candidates = [
      item?.apiId,
      item?.id,
      item?.legacyPresetId,
    ].map((value) => String(value || '').trim()).filter(Boolean);
    return candidates.includes(id);
  }) || null;
}

export function resolveNextModelEthnicityPresetSelection({
  presets = [],
  selectedPresetId = '',
  modelType,
  ethnicity,
  allowAutoSelect = true,
} = {}) {
  const comboPresets = filterModelEthnicityPresetsForCombo(presets, modelType, ethnicity);
  const selectable = filterSelectableModelEthnicityPresets(comboPresets);
  if (selectable.length === 0) return '';
  const id = String(selectedPresetId || '').trim();
  if (id) {
    const matched = findModelEthnicityPresetInList(selectable, id);
    if (matched && isModelEthnicityPresetForCombo(matched, modelType, ethnicity)) {
      return String(matched.id || id);
    }
  }
  if (!allowAutoSelect) return '';
  return String(selectable[0]?.id || '');
}

/** 节点 13.3 / 14.3：系统模特持久化 URL（与试衣 12.4 一致，优先 uploads 路径） */
export function resolveModelEthnicityPresetPersistedUrl(preset) {
  return resolveTryOnPresetPersistedUrl(preset);
}

/** 节点 13.3 / 14.3：选中系统模特时写入的任务参数字段 */
export function buildModelEthnicitySystemModelSelectionParams(preset) {
  return buildTryOnSystemModelSelectionParams(preset);
}

export function buildModelEthnicityUploadedModelSelectionParams() {
  return buildTryOnUploadedModelSelectionParams();
}

export function isModelEthnicitySystemPresetSelectorPaused(options = {}) {
  return isTryOnSystemPresetSelectorPaused(options);
}

/** 节点 13.3 / 14.3：手持/万物穿戴任务 initialResult / 工作流参数中的模特来源字段（同试衣 12.4 规范） */
export function buildModelEthnicityTaskModelParams({ uploadedModelRef, systemPreset } = {}) {
  return buildTryOnTaskModelParams({ uploadedModelRef, systemPreset });
}

/** 复用/重试时区分「用户上传模特」与「系统模特」 */
export function resolveModelEthnicityReuseModelState(result) {
  const r = result && typeof result === 'object' ? result : {};
  if (r.usedUploadedModel === true) {
    return { usedUploadedModel: true, selectedPresetId: '' };
  }
  const savedPresetId = r.selectedPresetId ? String(r.selectedPresetId).trim() : '';
  if (r.usedUploadedModel === false) {
    return {
      usedUploadedModel: false,
      selectedPresetId: savedPresetId || inferModelEthnicityPresetIdFromAssetUrl(r.modelImageUrl),
    };
  }
  if (savedPresetId) {
    return { usedUploadedModel: false, selectedPresetId: savedPresetId };
  }
  const modelUrl = String(r.modelImageUrl || '').trim();
  if (isModelEthnicitySystemPresetAssetUrl(modelUrl)) {
    return {
      usedUploadedModel: false,
      selectedPresetId: inferModelEthnicityPresetIdFromAssetUrl(modelUrl),
    };
  }
  if (modelUrl && r.hasCustomModel) {
    return { usedUploadedModel: true, selectedPresetId: '' };
  }
  return { usedUploadedModel: false, selectedPresetId: '' };
}

/** 手持/万物穿戴任务中用于展示的模特参考图（系统 preset 或用户上传） */
export function resolveModelEthnicityModelPreviewUrl(result, previewOffset = 0) {
  const r = result && typeof result === 'object' ? result : {};
  const reuseModel = resolveModelEthnicityReuseModelState(r);
  const previews = Array.isArray(r.referencePreviews) ? r.referencePreviews : [];
  const modelFromPreviews = previews[previewOffset] || '';

  if (reuseModel.usedUploadedModel) {
    return String(r.modelImageUrl || modelFromPreviews || '').trim();
  }
  const presetUrl = String(r.selectedPresetUrl || '').trim();
  if (presetUrl) return presetUrl;
  return String(r.modelImageUrl || modelFromPreviews || '').trim();
}

export function resolveModelEthnicityPresetSelectionFields({
  presets = [],
  selectedPresetId = '',
  modelType,
  ethnicity,
  allowAutoSelect = true,
} = {}) {
  const nextPresetId = resolveNextModelEthnicityPresetSelection({
    presets,
    selectedPresetId,
    modelType,
    ethnicity,
    allowAutoSelect,
  });
  if (!nextPresetId) {
    return buildModelEthnicitySystemModelSelectionParams(null);
  }
  const preset = findModelEthnicityPresetInList(presets, nextPresetId);
  return buildModelEthnicitySystemModelSelectionParams(preset || { id: nextPresetId });
}

export function resolveModelEthnicityPresetSelection({
  presets = [],
  modelType,
  ethnicity,
  presetId,
} = {}) {
  const id = String(presetId || '').trim();
  if (!id) return null;
  return findModelEthnicityPresetInList(presets, id)
    || findModelEthnicityPresetById(modelType, ethnicity, id);
}

/**
 * 从工作流/任务参数还原系统模特（同步：须传入已加载的 presets 列表）。
 * 查找顺序：API id / legacy id → legacy URL → 静态表。
 */
export function resolveModelEthnicityPresetFromParamsSync(
  modelType,
  ethnicity,
  params = {},
  presets = [],
) {
  const presetId = String(params.selectedPresetId || '').trim();
  const savedUrl = String(params.selectedPresetUrl || '').trim();
  if (!presetId && !savedUrl) return null;

  if (presetId) {
    const found = resolveModelEthnicityPresetSelection({
      presets,
      modelType,
      ethnicity,
      presetId,
    });
    if (found && isModelEthnicityPresetForCombo(found, modelType, ethnicity)) {
      return found;
    }
  }

  if (savedUrl) {
    const byUrl = findTryOnPresetInListByUrl(presets, savedUrl);
    if (byUrl && isModelEthnicityPresetForCombo(byUrl, modelType, ethnicity)) {
      return byUrl;
    }
    return {
      id: presetId,
      modelType,
      ethnicity,
      url: savedUrl,
      source: 'saved',
    };
  }

  return null;
}

/**
 * 从工作流/任务参数还原系统模特（异步：拉取 /api/system/models）。
 */
export async function resolveModelEthnicityPresetFromParams(
  featureCode,
  modelType,
  ethnicity,
  params = {},
  options = {},
) {
  const presetId = String(params.selectedPresetId || '').trim();
  if (!presetId && !params.selectedPresetUrl) return null;

  let presets = options.presets;
  if (!Array.isArray(presets)) {
    const fetched = await fetchModelEthnicityPresetsWithStaticFallback({
      featureCode,
      modelType,
      ethnicity,
    });
    presets = fetched.presets;
  }
  return resolveModelEthnicityPresetFromParamsSync(modelType, ethnicity, params, presets);
}

export function resolveEthnicityPresetFeatureCode(navId) {
  if (navId === 'modelHandheld') return SYSTEM_MODEL_ETHNICITY_FEATURE.modelHandheld;
  if (navId === 'modelUniversalWear') return SYSTEM_MODEL_ETHNICITY_FEATURE.modelUniversalWear;
  return '';
}
